import { datadogRum } from '@datadog/browser-rum';
import { useMemo, useState } from 'react';
import { ProductFruits } from 'react-product-fruits';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect, useLocation } from 'react-use';

import { useGetEnabledFeatureFlagsQuery } from '@savgroup-front-common/core/api/Authorization/useGetEnabledFeatureFlagsQuery';
import { useNewUiContext } from '@savgroup-front-common/core/components/NewUiProvider/NewUiProvider';
import { selectLocale } from '@savgroup-front-common/core/domains/i18n/selectors';
import {
  useCurrentUser,
  useLoginStore,
} from '@savgroup-front-common/core/domains/login/useLoginStore';
import {
  buildErrorNotification,
  getUniqErrorMessages,
} from '@savgroup-front-common/core/helpers';

import { PRODUCT_FRUITS_CONFIG } from '../../../configuration/productFruits';
import { signIn } from '../../../domains/login/actions';
import { useAppContext } from '../App.context';
import { SESSION_STATE } from '../App.context.constants';
import {
  clearSessionLoginAttempt,
  hasAttemptedSessionLogin,
  markSessionLoginAttempt,
} from '../App.context.helpers';
import Layout from '../layout/Layout';

import { useIsCypressRunning } from './CheckAuthorized.hooks';
import LoadingScreen from './LoadingScreen';
import messages from './messages';
import { NotRegisteredUser } from './NotRegisteredUser/NotRegisteredUser';

export const SessionCheckAuthorized = () => {
  const locale = useSelector(selectLocale);
  const { pathname, search } = useLocation();
  const invitationId = new URLSearchParams(search).get('invitation_id');
  const invitationToken = new URLSearchParams(search).get('invitation_token');
  const authenticatedUser = useCurrentUser();
  const setLoginStore = useLoginStore((state) => state.setLoginStore);
  const errors = authenticatedUser?.errors;

  const { isDarkModeEnabled } = useNewUiContext();
  const { isAuthenticated, sessionState, login, selectedSeller } =
    useAppContext();
  const [isNotRegisteredUser, setIsNotRegisteredUser] = useState(false);
  const [hasLoginFailed, setHasLoginFailed] = useState(false);
  const sellerId = selectedSeller?.id;
  const dispatch = useDispatch();
  const isCypressRunning = useIsCypressRunning();
  const featureFlags = useGetEnabledFeatureFlagsQuery({
    sellerId: sellerId ?? '',
    enabled: isAuthenticated && !!sellerId,
  });

  const userInfo = useMemo(
    () => ({
      username: authenticatedUser?.mail,
      email: authenticatedUser?.mail,
      firstname: authenticatedUser?.firstname,
      lastname: authenticatedUser?.lastname,
      props: {
        roles: authenticatedUser?.roles,
        featureFlags,
      },
    }),
    [
      authenticatedUser?.mail,
      authenticatedUser?.firstname,
      authenticatedUser?.lastname,
      authenticatedUser?.roles,
      featureFlags,
    ],
  );

  useDeepCompareEffect(() => {
    if (sessionState === SESSION_STATE.ABSENT) {
      if (hasAttemptedSessionLogin()) {
        clearSessionLoginAttempt();
        setHasLoginFailed(true);

        return;
      }

      markSessionLoginAttempt();
      login({
        extraQueryParams:
          invitationId && invitationToken
            ? {
                invitation_id: invitationId,
                invitation_token: invitationToken,
              }
            : undefined,
        returnTo: `${pathname}${search}`,
      });

      return;
    }

    if (sessionState !== SESSION_STATE.ACTIVE) {
      return;
    }

    clearSessionLoginAttempt();

    if (!isNotRegisteredUser && !authenticatedUser?.isLoaded) {
      dispatch(
        signIn({
          onGetUserForbidden: () => setIsNotRegisteredUser(true),
          setLoginStore,
        }) as any,
      );
    }
  }, [
    dispatch,
    invitationId,
    invitationToken,
    isNotRegisteredUser,
    sessionState,
    login,
    pathname,
    search,
    authenticatedUser,
    setLoginStore,
  ]);

  const notifications = getUniqErrorMessages(errors || []).map(
    buildErrorNotification,
  );

  if (isNotRegisteredUser) {
    return <NotRegisteredUser />;
  }

  if (hasLoginFailed) {
    return (
      <LoadingScreen
        notifications={[
          ...notifications,
          buildErrorNotification(messages.sessionLoginFailed),
        ]}
      />
    );
  }

  if (sessionState === SESSION_STATE.UNAVAILABLE) {
    return (
      <LoadingScreen
        notifications={[
          ...notifications,
          buildErrorNotification(messages.sessionStateUnavailable),
        ]}
      />
    );
  }

  if (!isAuthenticated || !authenticatedUser?.isLoaded) {
    return <LoadingScreen notifications={notifications} />;
  }

  datadogRum.setUser({
    id: authenticatedUser.userId,
    name: `${authenticatedUser.firstname} ${authenticatedUser.lastname}`,
    email: authenticatedUser.mail,
    accesses: authenticatedUser.accesses,
    permissions: authenticatedUser.permissions,
    isDarkModeEnabled,
  });

  return (
    <>
      <Layout />

      {!isCypressRunning && PRODUCT_FRUITS_CONFIG.workspaceCode && (
        <ProductFruits
          workspaceCode={PRODUCT_FRUITS_CONFIG.workspaceCode}
          language={locale}
          user={userInfo as any}
        />
      )}
    </>
  );
};
